export type CheckoutFlow = 'plan' | 'credits';
export type CheckoutReturnFlag = 'billing_success' | 'billing_cancelled';
export type CheckoutReturnOutcome = 'success' | 'cancelled';

export const CHECKOUT_RETURN_FLAG_KEYS: CheckoutReturnFlag[] = ['billing_success', 'billing_cancelled'];
export const CHECKOUT_SESSION_KEY = 'session_id';
export const CHECKOUT_FLOW_KEY = 'checkout_flow';
export const CHECKOUT_RETURN_KEYS = [
  ...CHECKOUT_RETURN_FLAG_KEYS,
  CHECKOUT_SESSION_KEY,
  CHECKOUT_FLOW_KEY,
] as const;

export interface CheckoutReturnContext {
  isReturn: boolean;
  isValid: boolean;
  sessionId: string | null;
  flow: CheckoutFlow | null;
  outcome: CheckoutReturnOutcome | null;
}

export type PlanCheckoutStatusReturnContext = CheckoutReturnContext & {
  isValid: true;
  sessionId: string;
  flow: 'plan';
  outcome: 'success';
};

const normalizeFlow = (value: string | null): CheckoutFlow | null => {
  const normalized = (value || '').trim().toLowerCase();
  return normalized === 'plan' || normalized === 'credits' ? normalized : null;
};

export const hasCheckoutReturnFlag = (params: URLSearchParams): boolean =>
  CHECKOUT_RETURN_FLAG_KEYS.some((key) => params.has(key));

export const parseCheckoutReturnParams = (search: string | URLSearchParams): CheckoutReturnContext => {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const isSuccess = params.has('billing_success');
  const isCancelled = params.has('billing_cancelled');
  const sessionId = (params.get(CHECKOUT_SESSION_KEY) || '').trim() || null;
  const flow = normalizeFlow(params.get(CHECKOUT_FLOW_KEY));
  const outcome: CheckoutReturnOutcome | null = isSuccess ? 'success' : isCancelled ? 'cancelled' : null;
  const isReturn = Boolean(outcome);
  const hasRequiredSession = outcome === 'cancelled' || Boolean(sessionId);
  return {
    isReturn,
    isValid: Boolean(isReturn && flow && hasRequiredSession && !(isSuccess && isCancelled)),
    sessionId,
    flow,
    outcome,
  };
};

export const hasCheckoutReturnParams = (search: string | URLSearchParams): boolean =>
  parseCheckoutReturnParams(search).isReturn;

export const getCheckoutReturnDestination = (
  pathname: string,
  search: string,
): { pathname: string; search: string } | null => {
  const context = parseCheckoutReturnParams(search);
  if (!context.isReturn || pathname.replace(/\/+$/, '') === '/billing') return null;

  // Even malformed returns belong in Billing, where they can be explained and
  // cleared without interpreting a success flag as proof of payment.
  const params = new URLSearchParams(search);
  params.delete('plan');
  params.set('section', context.flow === 'credits' ? 'credits' : 'plans');
  return { pathname: '/billing', search: `?${params}` };
};

export const isPlanCheckoutStatusReturn = (
  context: CheckoutReturnContext,
): context is PlanCheckoutStatusReturnContext =>
  Boolean(context.isValid && context.flow === 'plan' && context.outcome === 'success' && context.sessionId);

export const removeCheckoutReturnParams = (params: URLSearchParams): boolean => {
  let changed = false;
  CHECKOUT_RETURN_KEYS.forEach((key) => {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  });
  return changed;
};

export const buildCheckoutReturnUrl = (
  href: string,
  flag: CheckoutReturnFlag,
  flow: CheckoutFlow,
): string => {
  const callbackUrl = new URL(href);
  removeCheckoutReturnParams(callbackUrl.searchParams);
  callbackUrl.searchParams.set(flag, 'true');
  callbackUrl.searchParams.set(CHECKOUT_FLOW_KEY, flow);
  return callbackUrl.toString();
};

export const clearCheckoutReturnParamsFromUrl = (href: string): URL => {
  const returnUrl = new URL(href);
  removeCheckoutReturnParams(returnUrl.searchParams);
  return returnUrl;
};
